import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useActivity } from '../App';

const apiCall = () => axios.create({
  baseURL: 'http://localhost:8080',
  headers: { Authorization: `Bearer ${localStorage.getItem('wb_admin_token')}` }
});

const DEFAULT_CONFIG = {
  coinPerKm: 0,
  minWithdrawalCoins: 0,
  defaultCommission: 0,
  maxBattleParticipants: 0,
  referralBonus: 0,
  dailyChallengeCoins: 0,
  walkRewardEnabled: true,
  referralEnabled: true,
  battleCreationEnabled: true,
  maintenanceMode: false,
};

const AppConfig = () => {
  const { logActivity } = useActivity?.() || {};
  const [config, setConfig]       = useState(DEFAULT_CONFIG);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await apiCall().get('/api/admin/config');
      const data = res.data?.data || res.data || {};
      setConfig(prev => ({ ...prev, ...data }));
    } catch {
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleNumberChange = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value === '' ? '' : Number(value) }));
  };

  const handleToggle = (key) => {
    setConfig(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiCall().put('/api/admin/config', config);
      toast.success('Configuration saved successfully');
      setLastSaved(new Date());
    } catch {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-in" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '40vh' }}>
        <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: '2rem', marginBottom: '12px' }}>⚙️</div>
          <div>Loading configuration…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-in">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <div className="breadcrumb"><span>System</span> / <span className="current">App Configuration</span></div>
          <h1>⚙️ App Configuration</h1>
          <p style={{ color: 'var(--text-secondary)' }}>Manage global settings, coin economy, and feature flags</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
          <button
            style={{ ...primaryBtnS, opacity: saving ? 0.7 : 1 }}
            disabled={saving}
            onClick={handleSave}
          >
            {saving ? '⏳ Saving…' : '💾 Save Changes'}
          </button>
          {lastSaved && (
            <span style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>
              Last saved: {lastSaved.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
        </div>
      </div>

      {/* Warning Banner */}
      <div style={warningBannerS}>
        <span style={{ fontSize: '1.1rem' }}>⚠️</span>
        <span style={{ fontSize: '0.86rem', fontWeight: 600 }}>
          Changes take effect immediately — no server restart needed
        </span>
      </div>

      {/* Section 1: Coin & Economy Settings */}
      <div style={sectionS}>
        <div style={sectionHeaderS}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>🪙 Coin &amp; Economy Settings</h2>
          <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
            Control coin earning rates, withdrawal limits, and battle parameters
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '18px', marginTop: '20px' }}>
          {[
            { key: 'coinPerKm',            label: 'Coins per KM Walked',           desc: 'Reward coins awarded per kilometer walked',        icon: '🚶' },
            { key: 'minWithdrawalCoins',   label: 'Minimum Coins for Withdrawal',  desc: 'Minimum balance required to request a withdrawal', icon: '💸' },
            { key: 'referralBonus',        label: 'Referral Bonus Coins',          desc: 'Coins awarded when a referred user signs up',      icon: '👥' },
            { key: 'dailyChallengeCoins',  label: 'Daily Challenge Reward Coins',  desc: 'Coins given for completing the daily challenge',   icon: '📅' },
            { key: 'defaultCommission',    label: 'Default Battle Commission %',   desc: 'Platform fee percentage taken from battle rewards', icon: '⚔️' },
            { key: 'maxBattleParticipants',label: 'Max Participants per Battle',   desc: 'Maximum number of users that can join one battle',  icon: '👤' },
          ].map(({ key, label, desc, icon }) => (
            <div key={key} style={fieldGroupS}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                <span style={{ fontSize: '1rem' }}>{icon}</span>
                <span style={{ fontSize: '0.84rem', fontWeight: 700, color: 'var(--text-primary)' }}>{label}</span>
              </label>
              <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginBottom: '8px' }}>{desc}</div>
              <input
                type="number"
                min="0"
                style={numInputS}
                value={config[key] ?? ''}
                onChange={e => handleNumberChange(key, e.target.value)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Section 2: Feature Flags */}
      <div style={{ ...sectionS, marginTop: '20px' }}>
        <div style={sectionHeaderS}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>🚦 Feature Flags</h2>
          <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
            Enable or disable app features in real-time
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0', marginTop: '16px' }}>
          {[
            { key: 'walkRewardEnabled',     label: 'Walk Reward System',   desc: 'Coins given per km walked by users',                    isDanger: false },
            { key: 'referralEnabled',       label: 'Referral Program',     desc: 'Users can refer friends using their referral code',     isDanger: false },
            { key: 'battleCreationEnabled', label: 'Battle Creation',      desc: 'Users can create and join walking battles',             isDanger: false },
            { key: 'maintenanceMode',       label: 'Maintenance Mode',     desc: 'Disables the app for all users — use with caution',     isDanger: true  },
          ].map(({ key, label, desc, isDanger }, idx, arr) => (
            <div
              key={key}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 0',
                borderBottom: idx < arr.length - 1 ? '1px solid var(--border-color)' : 'none',
              }}
            >
              <div style={{ flex: 1, paddingRight: '20px' }}>
                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: isDanger ? '#ef4444' : 'var(--text-primary)' }}>
                  {isDanger && <span style={{ marginRight: '6px' }}>🔴</span>}{label}
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '3px' }}>{desc}</div>
              </div>
              <div
                role="switch"
                aria-checked={!!config[key]}
                tabIndex={0}
                onClick={() => handleToggle(key)}
                onKeyDown={e => (e.key === ' ' || e.key === 'Enter') && handleToggle(key)}
                style={{
                  width: '48px',
                  height: '26px',
                  borderRadius: '13px',
                  background: config[key]
                    ? (isDanger ? '#ef4444' : '#22c55e')
                    : 'var(--bg-tertiary)',
                  border: `2px solid ${config[key] ? (isDanger ? '#ef4444' : '#22c55e') : 'var(--border-color)'}`,
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'background 0.2s, border-color 0.2s',
                  flexShrink: 0,
                  outline: 'none',
                }}
              >
                <div style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  background: 'white',
                  position: 'absolute',
                  top: '2px',
                  left: config[key] ? '24px' : '2px',
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Save */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px', paddingBottom: '32px' }}>
        <button
          style={{ ...primaryBtnS, padding: '11px 28px', fontSize: '0.92rem', opacity: saving ? 0.7 : 1 }}
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? '⏳ Saving…' : '💾 Save All Changes'}
        </button>
      </div>
    </div>
  );
};

const primaryBtnS    = { padding: '9px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white' };
const warningBannerS = { display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 18px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '10px', marginBottom: '20px', color: '#f59e0b' };
const sectionS       = { background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '24px' };
const sectionHeaderS = { borderBottom: '1px solid var(--border-color)', paddingBottom: '16px' };
const fieldGroupS    = { display: 'flex', flexDirection: 'column' };
const numInputS      = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '0.92rem', outline: 'none', fontWeight: 600 };

export default AppConfig;
